import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import {
  Activity,
  Apple,
  BookOpen,
  Bot,
  Brain,
  BriefcaseBusiness,
  Dumbbell,
  Droplets,
  Flag,
  GraduationCap,
  Headphones,
  Instagram,
  Linkedin,
  MessageCircle,
  Moon,
  PenLine,
  Smartphone,
  Snowflake,
  Twitter,
  Users,
  Waves,
  Wind
} from 'lucide-react';
import { BRAND_NAME, DEFAULT_OG_IMAGE, ORGANIZATION_ID, absoluteUrl } from '../seo';
import './home.css';

function Home() {
  const pageUrl = absoluteUrl('/');
  const pageTitle = 'Activity-Specific Breathwork Courses';
  const pageDescription =
    'Train your breath for boxing, running, surfing, swimming, focus, and recovery with structured activity-based courses in Apex Breath.';

  const homeSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: `${pageTitle} | ${BRAND_NAME}`,
    description: pageDescription,
    url: pageUrl,
    isPartOf: {
      '@type': 'WebSite',
      url: absoluteUrl('/')
    },
    about: {
      '@type': 'SoftwareApplication',
      name: BRAND_NAME,
      applicationCategory: 'HealthApplication'
    }
  };

  const appSchema = {
    '@context': 'https://schema.org',
    '@type': 'MobileApplication',
    name: BRAND_NAME,
    operatingSystem: 'iOS',
    applicationCategory: 'HealthApplication',
    downloadUrl: 'https://apps.apple.com/us/app/apex-breath/id6743015773',
    offers: {
      '@type': 'Offer',
      price: '0',
      priceCurrency: 'USD'
    },
    publisher: {
      '@type': 'Organization',
      '@id': ORGANIZATION_ID
    },
    description: pageDescription
  };
  const topTicker = [
    { title: 'Boxing', icon: Activity, glow: '#f16618' },
    { title: 'Surfing', icon: Waves, glow: '#1e9bd8' },
    { title: 'Running', icon: Flag, glow: '#ff4f17' },
    { title: 'Swimming', icon: Droplets, glow: '#0da5b3' },
    { title: 'Cold Plunge', icon: Snowflake, glow: '#1899f1' },
    { title: 'Meditation', icon: Wind, glow: '#6e63ff' }
  ];

  const bottomTicker = [
    { title: 'Focus Work', icon: Brain, glow: '#8f2dff' },
    { title: 'HIIT Training', icon: Activity, glow: '#ff0ea8' },
    { title: 'Breath Holds', icon: Moon, glow: '#4f6aff' },
    { title: 'Mobility Flow', icon: Smartphone, glow: '#22a658' },
    { title: 'Pre-Game Calm', icon: PenLine, glow: '#9a7c53' },
    { title: 'Sleep Recovery', icon: BookOpen, glow: '#26b7d4' }
  ];

  const achievementCards = [
    {
      name: 'Noah',
      activity: 'Surfing',
      image: '/surfing_card.webp',
      result: 'Extended breath-hold sets between waves'
    },
    {
      name: 'Elena',
      activity: 'Boxing',
      image: '/boxing_card.webp',
      result: 'Sharper recovery between high-output rounds'
    },
    {
      name: 'Marcus',
      activity: 'Running',
      image: '/running_card.webp',
      result: 'Stronger pacing through longer tempo runs'
    },
    {
      name: 'Avery',
      activity: 'Swimming',
      image: '/swimming_card.webp',
      result: 'Better lap rhythm with calmer breathing'
    },
    {
      name: 'Jules',
      activity: 'Cold Plunge',
      image: '/Cold_card.webp',
      result: 'Steadier inhale control during cold exposure'
    },
    {
      name: 'Sofia',
      activity: 'Meditation',
      image: '/hero_bg.webp',
      result: 'Reached deeper calm in less time each day'
    }
  ];

  useEffect(() => {
    const sections = Array.from(document.querySelectorAll('.home-v2 section'));
    if (!sections.length) return undefined;

    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    sections.forEach((section) => section.classList.add('v2-scroll-section'));

    if (prefersReducedMotion) {
      sections.forEach((section) => section.classList.add('is-visible'));
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.intersectionRatio > 0.16) {
            entry.target.classList.add('is-visible');
          } else {
            entry.target.classList.remove('is-visible');
          }
        });
      },
      {
        threshold: [0, 0.16, 0.5, 0.85],
        rootMargin: '-8% 0px -8% 0px'
      }
    );

    sections.forEach((section) => observer.observe(section));

    return () => observer.disconnect();
  }, []);

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        <link rel="canonical" href={pageUrl} />
        <meta property="og:title" content={`${pageTitle} | ${BRAND_NAME}`} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:url" content={pageUrl} />
        <meta property="og:image" content={DEFAULT_OG_IMAGE} />
        <meta name="twitter:title" content={`${pageTitle} | ${BRAND_NAME}`} />
        <meta name="twitter:description" content={pageDescription} />
        <meta name="twitter:image" content={DEFAULT_OG_IMAGE} />
        <script type="application/ld+json">{JSON.stringify(homeSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(appSchema)}</script>
      </Helmet>

      <div className="home-v2">
        <section className="v2-hero" style={{ backgroundImage: `url(${process.env.PUBLIC_URL}/hero_bg.webp)` }}>
          <div className="v2-hero-overlay" />
          <div className="v2-hero-content">
            <p className="v2-pill">Apex Breath</p>
            <h1>Activity-Specific Breathwork That Improves Real Performance.</h1>
            <p>Train with guided breathing sessions built for boxing, running, surfing, swimming, and recovery.</p>
            <button type="button" className="v2-cta v2-cta-disabled" disabled>
              Coming Soon
            </button>
          </div>
          <div className="v2-hero-fade" />
        </section>

        <main className="v2-main">
          <section className="v2-center-copy">
            <h2>
              Apex Breath matches each session to the activity you are training for, so every breath practice has a
              clear purpose.
            </h2>
          </section>

          <section className="v2-ticker" aria-label="Apex Breath routine preview">
            <div className="v2-ticker-rows">
              <div className="v2-ticker-row">
                <div className="v2-track">
                  {[...topTicker, ...topTicker].map((item, index) => (
                    <article className="v2-ticker-card" key={`top-${index}`}>
                      <span className="v2-ticker-icon" style={{ '--glow': item.glow }}>
                        <item.icon size={24} strokeWidth={2.5} />
                      </span>
                      <p>{item.title}</p>
                    </article>
                  ))}
                </div>
              </div>
              <div className="v2-ticker-row v2-ticker-row-reverse">
                <div className="v2-track">
                  {[...bottomTicker, ...bottomTicker].map((item, index) => (
                    <article className="v2-ticker-card" key={`bottom-${index}`}>
                      <span className="v2-ticker-icon" style={{ '--glow': item.glow }}>
                        <item.icon size={24} strokeWidth={2.5} />
                      </span>
                      <p>{item.title}</p>
                    </article>
                  ))}
                </div>
              </div>
            </div>
            <img className="v2-ticker-phone" src="/apex_breath_no_bg.png" alt="Apex Breath iPhone preview" />
            <div className="v2-side-fade v2-side-fade-left" />
            <div className="v2-side-fade v2-side-fade-right" />
          </section>

          <section className="v2-layout-intro" id="features">
            <div>
              <p className="v2-badge">Habits with structure</p>
              <h3>Courses that train your breath with purpose.</h3>
            </div>
            <p>
              Apex Breath helps you train with purpose through activity-specific courses, clear progress tracking, and
              a structure that keeps your breathing practice consistent.
            </p>
          </section>

          <section className="v2-two-cards" id="use-cases">
            <article className="v2-card v2-card-light">
              <h4>Flexible streak rules</h4>
              <p>Build honest streaks that reflect real commitment to healthy living.</p>
              <img src="/profile_no_bg.png" alt="Apex Breath streak card" />
            </article>
            <article className="v2-card v2-card-dark">
              <h4>Smart course paths</h4>
              <p>Choose the right breathwork course for your activity, intensity, and recovery goals.</p>
              <img src="/course_img.png" alt="Apex Breath planner card" />
            </article>
          </section>

          <section className="v2-ai-showcase" id="ai-suggestions">
            <div className="v2-ai-top">
              <div className="v2-ai-left">
                <p className="v2-ai-pill">Activity-based training</p>
                <h3>Courses built for your active lifestyle</h3>
                <p>
                  Apex Breath gives you structured breathwork courses for performance, focus, and recovery so every
                  session has a clear purpose.
                </p>
              </div>
              <img src="/guy_breathing.png" alt="AI suggestions preview" />
            </div>

            <div className="v2-ai-grid">
              <article>
                <span className="v2-ai-dot v2-ai-blue" aria-hidden="true">
                  <Waves size={26} strokeWidth={2.4} />
                </span>
                <h4>Morning walk</h4>
                <p>Suggests the best time to remind you.</p>
              </article>
              <article>
                <span className="v2-ai-dot v2-ai-red" aria-hidden="true">
                  <Flag size={26} strokeWidth={2.4} />
                </span>
                <h4>Habit Priorities</h4>
                <p>Reorders habits on busy days.</p>
              </article>
              <article>
                <span className="v2-ai-dot v2-ai-purple" aria-hidden="true">
                  <Brain size={26} strokeWidth={2.4} />
                </span>
                <h4>Routine Insights</h4>
                <p>Highlights what's working and what's slipping.</p>
              </article>
              <article>
                <span className="v2-ai-dot v2-ai-green" aria-hidden="true">
                  <Activity size={26} strokeWidth={2.4} />
                </span>
                <h4>Recovery Suggestion</h4>
                <p>Helps you recover when you miss a day.</p>
              </article>
            </div>
          </section>

          <section className="v2-lifestyle">
            <p className="v2-lifestyle-pill">Built for real routines</p>
            <h3>Breathwork that fits your active lifestyle</h3>

            <div className="v2-lifestyle-tabs" role="tablist" aria-label="Lifestyle groups">
              <button type="button" className="is-active">
                <BriefcaseBusiness size={18} strokeWidth={2.2} />
                <span>Professionals</span>
              </button>
              <button type="button">
                <GraduationCap size={18} strokeWidth={2.2} />
                <span>Students</span>
              </button>
              <button type="button">
                <Dumbbell size={18} strokeWidth={2.2} />
                <span>Athletes</span>
              </button>
              <button type="button">
                <Users size={18} strokeWidth={2.2} />
                <span>Busy parents</span>
              </button>
            </div>

            <div className="v2-lifestyle-media">
              <img src="/lady_underwater.png" alt="Apex Breath lifestyle preview" />
              <aside className="v2-lifestyle-overlay">
                <p>Guided sessions for stress, focus, and recovery whether you have 3 minutes or 20.</p>
                <div>
                  <strong>12 min</strong>
                  <span>Average daily session</span>
                </div>
              </aside>
            </div>

            <p className="v2-lifestyle-caption">Breathwork routines that work across every kind of lifestyle</p>
            <p className="v2-lifestyle-tags">
              #athletes <span>#students</span> <span>#parents</span> <span>#founders</span>{' '}
              <span>#shift-workers</span> <span>#everyday-breathers</span>
            </p>
          </section>

          <section className="v2-user-wins" aria-label="What users are achieving with Apex Breath">
            <div className="v2-user-wins-head">
              <h3>What users are improving with Apex Breath</h3>
            </div>

            <div className="v2-user-wins-ticker">
              <div className="v2-user-wins-track">
                {[...achievementCards, ...achievementCards].map((card, index) => (
                  <article className="v2-user-win-card" key={`${card.activity}-${index}`}>
                    <img src={card.image} alt={`${card.name} training ${card.activity}`} />
                    <div className="v2-user-win-overlay" />
                    <div className="v2-user-win-content">
                      <p className="v2-user-win-name">
                        <strong>{card.name}</strong> <span>- {card.activity}</span>
                      </p>
                      <p className="v2-user-win-result">{card.result}</p>
                    </div>
                  </article>
                ))}
              </div>
            </div>
          </section>

          <section className="v2-globe" id="numbers">
            <p className="v2-globe-pill">Real habits, real numbers</p>
            <h3>Progress happens one breath at a time</h3>
            <p className="v2-big-number">2,000</p>
            <p className="v2-globe-chip">Minutes logged last month</p>
            <div className="v2-globe-media">
              <video src="/globe.mp4" autoPlay loop muted playsInline />
            </div>
            <div className="v2-globe-stats">
              <article>
                <strong>
                  87<span>%</span>
                </strong>
                <span>Felt steadier breathing in daily sessions</span>
              </article>
              <article>
                <strong>46</strong>
                <span>Average guided breathwork sessions / month</span>
              </article>
              <article>
                <strong>32+</strong>
                <span>Sport and activity-specific breathing courses</span>
              </article>
            </div>
          </section>

          <section className="v2-testimonials" id="testimonials">
            <h3>Testimonials</h3>
            <div className="v2-testimonial-grid">
              <article className="v2-testimonial-card">
                <span className="v2-testimonial-source v2-testimonial-source-ig" aria-label="From Instagram">
                  <Instagram size={16} strokeWidth={2.2} />
                </span>
                <p>"I finally have breathing sessions that match my real schedule."</p>
                <div className="v2-testimonial-person">
                  <strong>Maya Collins</strong>
                  <span>Mother, Florida</span>
                </div>
              </article>
              <article className="v2-testimonial-card">
                <span className="v2-testimonial-source v2-testimonial-source-x" aria-label="From X">
                  <Twitter size={16} strokeWidth={2.2} />
                </span>
                <p>"The planner keeps me consistent without being overwhelming."</p>
                <div className="v2-testimonial-person">
                  <strong>Derek Shaw</strong>
                  <span>Surfer, San Diego</span>
                </div>
              </article>
              <article className="v2-testimonial-card">
                <span className="v2-testimonial-source v2-testimonial-source-li" aria-label="From LinkedIn">
                  <Linkedin size={16} strokeWidth={2.2} />
                </span>
                <p>"Five minutes a day and I actually feel calmer before work."</p>
                <div className="v2-testimonial-person">
                  <strong>Priya Nair</strong>
                  <span>Graduate Student, Ohio</span>
                </div>
              </article>
              <article className="v2-testimonial-card">
                <span className="v2-testimonial-source v2-testimonial-source-chat" aria-label="From community post">
                  <MessageCircle size={16} strokeWidth={2.2} />
                </span>
                <p>"Best breathwork UX I have tried. Clear and simple."</p>
                <div className="v2-testimonial-person">
                  <strong>Lucas Bennett</strong>
                  <span>Runner, New York</span>
                </div>
              </article>
            </div>
          </section>

          <section className="v2-faq-block" id="faqs">
            <div className="v2-faq-left">
              <p className="v2-faq-pill">Common questions</p>
              <h3>Frequently asked questions</h3>
              <article className="v2-faq-contact">
                <span className="v2-faq-contact-icon">
                  <Headphones size={24} strokeWidth={2.5} />
                </span>
                <h4>Can't find your answer?</h4>
                <a
                  href="https://www.merokeeventures.com/contact"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="v2-faq-contact-btn"
                >
                  Contact us
                </a>
              </article>
            </div>

            <div className="v2-faq-right">
              <details className="v2-faq-item" open>
                <summary>
                  <span>How many habits can I track?</span>
                  <span className="v2-faq-toggle" aria-hidden="true" />
                </summary>
                <p>You can track as many as you want, but most users keep 5-8 habits for better consistency.</p>
              </details>
              <details className="v2-faq-item">
                <summary>
                  <span>Do reminders work across all devices?</span>
                  <span className="v2-faq-toggle" aria-hidden="true" />
                </summary>
                <p>Yes, reminders are synced for your account on supported devices where notifications are enabled.</p>
              </details>
              <details className="v2-faq-item">
                <summary>
                  <span>What happens if I miss a day?</span>
                  <span className="v2-faq-toggle" aria-hidden="true" />
                </summary>
                <p>You can pick back up immediately. Apex Breath is designed for consistency, not perfection.</p>
              </details>
              <details className="v2-faq-item">
                <summary>
                  <span>Can I create routines for different times of day?</span>
                  <span className="v2-faq-toggle" aria-hidden="true" />
                </summary>
                <p>Yes, you can structure sessions for morning focus, daytime performance, and evening recovery.</p>
              </details>
              <details className="v2-faq-item">
                <summary>
                  <span>Is Apex Breath free to use?</span>
                  <span className="v2-faq-toggle" aria-hidden="true" />
                </summary>
                <p>Apex Breath includes a free trial, then a subscription for full access.</p>
              </details>
            </div>
          </section>

          <section className="v2-download-cta">
            <div className="v2-download-copy">
              <h3>Build better habits with less effort</h3>
              <p>Track what matters, stay organized, and improve your breathing at your own pace.</p>
              <div className="v2-download-actions">
                <a
                  href="https://www.apple.com/app-store/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="v2-download-btn v2-download-btn-primary"
                >
                  <Apple size={18} strokeWidth={2.2} />
                  <span>Download for iPhone</span>
                </a>
                <a
                  href="https://play.google.com/store/apps"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="v2-download-btn v2-download-btn-secondary"
                >
                  <Bot size={18} strokeWidth={2.2} />
                  <span>Get it on Android</span>
                </a>
              </div>
            </div>

            <div className="v2-download-phone" aria-hidden="true">
              <img src="/course_img.png" alt="" />
            </div>

            <div className="v2-download-qr-wrap">
              <h4>Scan the QR code to download the app</h4>
              <div className="v2-fake-qr" aria-label="Decorative sample QR code">
                <span className="v2-qr-finder v2-qr-finder-tl" />
                <span className="v2-qr-finder v2-qr-finder-tr" />
                <span className="v2-qr-finder v2-qr-finder-bl" />
                <span className="v2-qr-center">
                  <img src="/apex_icon.jpeg" alt="" />
                </span>
              </div>
            </div>
          </section>

          <section className="v2-cloud-end" />
        </main>
      </div>
    </>
  );
}

export default Home;
